import React from 'react';
import { Chip, createFilterOptions } from '@mui/material';
import { AttachFile, ThumbUp, Done } from '@mui/icons-material';
import { SubscriptionContact } from 'store/api/models';
import { renderContactGroups } from './useProjectForm';
import { FormikHelpers, getIn } from 'formik';
import { ContactTooltip } from 'components/ContactTooltip';
import { NoMaxWidthTooltip } from '@centerpiece/components';
import { useEmailForm } from './useEmailForm';

export const useMessageForm = ({
  contacts = [],
  onDownload,
  setOpenContact,
  preview = false,
  reply = false,
  replied = false,
  forward = false,
  disabled,
  editing,
}: {
  contacts?: any[];
  onDownload: Function;
  setOpenContact?: Function;
  preview?: boolean;
  reply?: boolean;
  replied?: boolean;
  forward?: boolean;
  disabled?: boolean;
  editing?: boolean;
}) => {
  const messageBodyForm = useMessageBodyForm({
    contacts,
    onDownload,
    setOpenContact,
    preview: preview || reply,
    reply,
    replied,
    forward,
    disabled,
    editing,
  });

  return useEmailForm({
    contacts,
    onDownload,
    setOpenContact,
    preview,
    reply,
    replied,
    forward,
    disabled,
    messageBodyForm,
    editing,
  });
};

const useMessageBodyForm = ({
  contacts = [],
  onDownload,
  setOpenContact,
  preview = false,
  reply = false,
  replied = false,
  forward = false,
  disabled,
  editing,
}: {
  contacts?: any[];
  onDownload: Function;
  setOpenContact?: Function;
  preview?: boolean;
  reply?: boolean;
  replied?: boolean;
  forward?: boolean;
  disabled?: boolean;
  editing?: boolean;
}) => {
  preview = preview || reply;
  return [
    {
      name: 'form.body',
      label: 'Email Message',
      itemType: 'textfieldarray',
      disabled: disabled ? disabled : preview,
      gridProps: { xs: 12 },
      jItemProps: {
        // fab: {
        //   precondition: (values: any) => preview,
        //   name: 'attach_file',
        //   label: 'Attach file',
        //   icon: <AttachFile fontSize="small" />,
        //   itemTypes: 'file',
        // },
        preamble: {
          precondition: (values: any) => preview || replied,
          placeholder: 'Add your reply here',
          disabled: disabled && !preview ? disabled : false,
          action: {
            precondition: (values: any) => {
              const body = getIn(values, 'form.body') || [];
              return (
                (body?.filter((i: any) => i.type)?.length === 0 && preview) ||
                (replied && body?.[0]?.attachments?.length > 0)
              );
            },
            name: 'replyattachments',
            itemType: 'dropzone',
            label: 'Attach documents',
            disabled: disabled && !preview ? disabled : replied,
            gridProps: { xs: 12 },
            jItemProps: {
              hideDetails: reply || replied,
              onDownload: onDownload,
            },
          },
        },
        placeholder: `Add email message. 

Hover over the (+) button to insert a call to action. Below are the options:

Approve a file:
Use this to get approval on files and general information. Examples include shop drawings, submittals, design layouts, and more!

Confirm jobsite is ready:
Use this to confirm that a jobsite is ready for installation!

Share a file:
Use this to share files that are too large for email attachments. Examples include operating manuals, warranty info, and other close-out documentation.
`,
        // Response field:
        // Use this to highlight a place in you message you would like a response or comment.
        actionsprecondition: (values: any) => !reply && !replied,
        actions: {
          // response_field: {
          //   name: 'response_field',
          //   label: 'Response Field',
          //   icon: <InsertComment fontSize="small" />,
          //   form: {
          //     name: 'actions',
          //     itemType: 'paper',
          //     label: 'coordination items',
          //     arrayProps: { wrap: true },
          //     defaultValue: [{}],
          //     jItemProps: {
          //       elevation: 0,
          //     },
          //     array: [
          //       {
          //         name: 'reactiondetails',
          //         itemType: 'textfield',
          //         label: 'Please add any comments to the above request here',
          //         gridProps: { xs: 12 },
          //         jItemProps: {
          //           multiline: true,
          //           minRows: 3,
          //         },
          //         disabled: disabled ? disabled : !preview,
          //       },
          //     ],
          //   },
          // },
          share_file: {
            name: 'share_file',
            label: 'Share file',
            icon: <AttachFile fontSize="small" />,
            form: {
              name: 'actions',
              itemType: 'paper',
              label: 'coordination items',
              arrayProps: { wrap: true },
              defaultValue: [{}],
              jItemProps: {
                paper: { elevation: 0 },
              },
              array: [
                {
                  name: 'attachments',
                  itemType: 'dropzone',
                  label: 'Attach documents',
                  disabled: disabled ? disabled : preview || replied,
                  gridProps: { xs: 12 },
                  jItemProps: {
                    hideDetails: reply || replied,
                    onDownload: onDownload,
                    onDelete: editing ? () => {} : undefined,
                  },
                },
                {
                  precondition: (values: any) => {
                    const action = getIn(values, values.name);
                    return (
                      preview ||
                      (replied &&
                        action?.reactionattachments &&
                        action.reactionattachments?.length > 0)
                    );
                  },
                  name: 'share-file-reply-options',
                  label: replied ? 'Replied attachments' : 'Reply attachments',
                  itemType: 'typography',
                  jItemProps: { variant: 'overline' },
                  gridProps: {
                    sx: { marginTop: '0px !important' },
                  },
                },
                {
                  precondition: (values: any) => {
                    const action = getIn(values, values.name);
                    return (
                      preview ||
                      (replied &&
                        action?.reactionattachments &&
                        action.reactionattachments?.length > 0)
                    );
                  },
                  name: 'reactionattachments',
                  itemType: 'dropzone',
                  label: 'Attach documents',
                  disabled: disabled ? disabled : replied,
                  gridProps: { xs: 12 },
                  jItemProps: {
                    hideDetails: reply || replied,
                    onDownload: onDownload,
                  },
                },
                {
                  name: 'reactiondetails',
                  itemType: 'textfield',
                  label:
                    'Please add any comments on the above attachment(s) here',
                  gridProps: { xs: 12 },
                  jItemProps: {
                    multiline: true,
                    minRows: 3,
                  },
                  disabled: disabled ? disabled : !preview,
                },
              ],
            },
          },
          readiness_request: {
            name: 'readiness_request',
            label: 'Confirm jobsite is ready',
            icon: <Done fontSize="small" />,
            form: {
              name: 'actions',
              itemType: 'paper',
              label: 'coordination items',
              arrayProps: { wrap: true },
              defaultValue: [{}],
              jItemProps: {
                paper: {
                  elevation: 0,
                  sx: { pt: 0, mt: 0 },
                },
              },
              array: [
                // {
                //   name: 'title',
                //   label: 'Title your coordination item',
                //   itemType: 'textfield',
                //   gridProps: { xs: 12 },
                // },
                // {
                //   name: 'details',
                //   itemType: 'textfield',
                //   label: 'Add coordination item details here',
                //   gridProps: { xs: 12 },
                //   jItemProps: {
                //     multiline: true,
                //     minRows: 3,
                //     maxRows: 20,
                //   },
                // },
                {
                  name: 'readiness-reply-options',
                  label: 'Confirm jobsite is ready',
                  itemType: 'typography',
                  jItemProps: { variant: 'overline' },
                  gridProps: {
                    sx: { marginTop: '0px !important' },
                  },
                },
                {
                  name: 'status',
                  itemType: 'toggle_buttons',
                  label: '',
                  disabled: disabled ? disabled : !preview,
                  options: ['Ready', 'Not Ready', 'Call Me'],
                  jItemProps: {
                    itemsProps: [
                      { gridProps: { xs: 12, sm: 4 } },
                      { gridProps: { xs: 12, sm: 4 } },
                      { gridProps: { xs: 12, sm: 4 } },
                    ],
                  },
                },
                {
                  name: 'attachments',
                  itemType: 'dropzone',
                  label: 'Attach documents',
                  disabled: disabled ? disabled : preview || replied,
                  gridProps: { xs: 12 },
                  jItemProps: {
                    hideDetails: reply || replied,
                    onDownload: onDownload,
                    onDelete: editing ? () => {} : undefined,
                  },
                },
                {
                  precondition: (values: any) => {
                    const action = getIn(values, values.name);
                    return (
                      preview ||
                      (replied &&
                        action?.reactionattachments &&
                        action.reactionattachments?.length > 0)
                    );
                  },
                  name: 'readiness-reply-attachments',
                  label: replied ? 'Replied attachments' : 'Reply attachments',
                  itemType: 'typography',
                  jItemProps: { variant: 'overline' },
                  gridProps: {
                    sx: { marginTop: '0px !important' },
                  },
                },
                {
                  precondition: (values: any) => {
                    const action = getIn(values, values.name);
                    return (
                      preview ||
                      (replied &&
                        action?.reactionattachments &&
                        action.reactionattachments?.length > 0)
                    );
                  },
                  name: 'reactionattachments',
                  itemType: 'dropzone',
                  label: 'Attach documents',
                  disabled: disabled ? disabled : replied,
                  gridProps: { xs: 12 },
                  jItemProps: {
                    hideDetails: reply || replied,
                    onDownload: onDownload,
                  },
                },
                {
                  name: 'reactiondetails',
                  itemType: 'textfield',
                  label:
                    'Please select an option above. Add any additional comments here',
                  gridProps: { xs: 12 },
                  jItemProps: {
                    multiline: true,
                    minRows: 3,
                  },
                  disabled: disabled ? disabled : !preview,
                },
              ],
            },
          },
          approval_request: {
            name: 'approval_request',
            label: 'Approve a file',
            icon: <ThumbUp fontSize="small" />,
            form: {
              name: 'actions',
              itemType: 'paper',
              label: 'coordination items',
              arrayProps: { wrap: true },
              defaultValue: [{}],
              jItemProps: {
                paper: { elevation: 0 },
              },
              array: [
                // {
                //   name: 'title',
                //   label: 'Title your coordination item',
                //   itemType: 'textfield',
                //   gridProps: { xs: 12 },
                // },
                // {
                //   name: 'details',
                //   itemType: 'textfield',
                //   label: 'Add coordination item details here',
                //   gridProps: { xs: 12 },
                //   jItemProps: {
                //     multiline: true,
                //     minRows: 3,
                //     maxRows: 20,
                //   },
                // },
                {
                  name: 'approval-reply-options',
                  label: 'Approve a file',
                  itemType: 'typography',
                  jItemProps: { variant: 'overline' },
                  gridProps: {
                    sx: { marginTop: '0px !important' },
                  },
                },
                {
                  name: 'status',
                  itemType: 'toggle_buttons',
                  label: '',
                  disabled: disabled ? disabled : !preview,
                  options: [
                    'Approved',
                    'Approved with Markups',
                    'Rejected',
                    'Call Me',
                  ],
                  jItemProps: {
                    itemsProps: [
                      {
                        gridProps: {
                          sx: { width: { xs: '100%', sm: '50%', md: '20%' } },
                        },
                      },
                      {
                        gridProps: {
                          sx: { width: { xs: '100%', sm: '50%', md: '41%' } },
                        },
                      },
                      {
                        gridProps: {
                          sx: { width: { xs: '100%', sm: '50%', md: '20%' } },
                        },
                      },
                      {
                        gridProps: {
                          sx: { width: { xs: '100%', sm: '50%', md: '19%' } },
                        },
                      },
                    ],
                  },
                },
                {
                  name: 'attachments',
                  itemType: 'dropzone',
                  label: 'Attach documents',
                  disabled: disabled ? disabled : preview || replied,
                  gridProps: { xs: 12 },
                  jItemProps: {
                    hideDetails: reply || replied,
                    onDownload: onDownload,
                    onDelete: editing ? () => {} : undefined,
                  },
                },
                {
                  precondition: (values: any) => {
                    const action = getIn(values, values.name);
                    return (
                      preview ||
                      (replied &&
                        action?.reactionattachments &&
                        action.reactionattachments?.length > 0)
                    );
                  },
                  name: 'approval-reply-attachments',
                  label: replied ? 'Replied attachments' : 'Reply attachments',
                  itemType: 'typography',
                  jItemProps: { variant: 'overline' },
                  gridProps: {
                    sx: { marginTop: '0px !important' },
                  },
                },
                {
                  precondition: (values: any) => {
                    const action = getIn(values, values.name);
                    return (
                      preview ||
                      (replied &&
                        action?.reactionattachments &&
                        action.reactionattachments?.length > 0)
                    );
                  },
                  name: 'reactionattachments',
                  itemType: 'dropzone',
                  label: 'Attach documents',
                  disabled: disabled ? disabled : replied,
                  gridProps: { xs: 12 },
                  jItemProps: {
                    hideDetails: preview || replied,
                    onDownload: onDownload,
                  },
                },
                {
                  // precondition: (values: any) => preview,
                  name: 'reactiondetails',
                  itemType: 'textfield',
                  label:
                    'Please select an option above. Add any additional comments here',
                  gridProps: { xs: 12 },
                  jItemProps: {
                    multiline: true,
                    rows: 2,
                  },
                  disabled: disabled ? disabled : !preview,
                },
              ],
            },
          },
        },
      },
    },
  ];
};
